"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.PERSONALIZATION_KEY_NAME = void 0;
exports.default = DomainSearch;
var _extends2 = _interopRequireDefault(require("@babel/runtime/helpers/extends"));
var _react = _interopRequireWildcard(require("react"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _domainSearchBox = require("@site/domain-search-box");
var _core = require("@linaria/core");
var _educationalDropdown = _interopRequireDefault(require("../experiments/educational-dropdown"));
var _icon = require("@site/pattern-library-utils/icon");
var _intents = _interopRequireDefault(require("@site/pattern-library-utils/intents"));
var _layers = require("@site/pattern-library-utils/layers");
var _assets = require("@site/pattern-library-utils/assets");
var _base = require("@site/pattern-library-utils/base.functions");
var _keyboard = require("@site/pattern-library-utils/keyboard");
var _tracking = _interopRequireDefault(require("@site/pattern-library-utils/tracking"));
var _breakpoints = require("@site/pattern-library-utils/breakpoints");
var _patternLibraryUtils = require("@site/pattern-library-utils");
var _overlayPortal = _interopRequireDefault(require("../experiments/educational-dropdown/overlay-portal"));
function _getRequireWildcardCache(e) { if ("function" != typeof WeakMap) return null; var r = new WeakMap(), t = new WeakMap(); return (_getRequireWildcardCache = function (e) { return e ? t : r; })(e); }
function _interopRequireWildcard(e, r) { if (!r && e && e.__esModule) return e; if (null === e || "object" != typeof e && "function" != typeof e) return { default: e }; var t = _getRequireWildcardCache(r); if (t && t.has(e)) return t.get(e); var n = { __proto__: null }, a = Object.defineProperty && Object.getOwnPropertyDescriptor; for (var u in e) if ("default" !== u && {}.hasOwnProperty.call(e, u)) { var i = a ? Object.getOwnPropertyDescriptor(e, u) : null; i && (i.get || i.set) ? Object.defineProperty(n, u, i) : n[u] = e[u]; } return n.default = e, t && t.set(e, n), n; }
/* eslint-disable complexity */

// TODO CMS-23047
const PERSONALIZATION_KEY_NAME = exports.PERSONALIZATION_KEY_NAME = 'tmskey';
const domainSearchStyles = "core-d1aadcj2";
const iconStyles = "core-i1jc7lyy";
const overlayClass = "core-oikm6up";
const elevatedWrapperClass = "core-ez525cw";

/**
 * Domain Search component.
 * @module DomainSearch
 *
 * @param {Object} props React Props
 * @param {string} props._name Sitecore item name for tracking
 * @param {string} props.env The domain search env
 * @param {string} props.url The search url
 * @param {string} props.urlParams The search url params
 * @param {string} props.endpointType The search endpoint type
 * @param {string} props.personalizationKey The personalization key
 * @param {string} props.searchFieldName Search field name
 * @param {bool} props.checkAvail Wether to check availability
 * @param {string} props.ariaLabelButton Button Aria Label
 * @param {string} props.ariaLabelInput Input Aria Label
 * @param {string} props.buttonText Button text
 * @param {string} props.height Height of the search box
 * @param {bool} props.isPromo Use promo (default) or eid
 * @param {(bool)} props.browserAutoComplete Turn autocomplete on or off
 * @param {("query"|"domain"|".")} props.autocompleteMode Autocomplete mode
 * @param {object} props.trackingCustomProperties Custom properties object
 * @param {string} props.selectedTld Selected TLD to append to search query
 * @param {bool} props.enableEducationalDropdown Enable educational dropdown with suggestions (default: false)
 * @param {array} props.educationalDropdownContentCards Content cards configuration for educational dropdown
 * @param {number} props.maxResults Maximum number of suggestions to display (default: 6)
 * @returns {React.ReactElement} DomainSearch component.
 */
function DomainSearch({
  id,
  _name,
  endpointType,
  urlParams,
  env = 'prod',
  url,
  checkAvail,
  personalizationKey,
  ariaLabelButton = 'Search',
  ariaLabelInput,
  browserAutoComplete,
  autocompleteMode,
  buttonText,
  height = 'default',
  icon,
  isPromo = true,
  handleOnSearch,
  trackingCustomProperties = {},
  prefetchAssets = [],
  prefetchSerpAssets = false,
  partial,
  selectedTld,
  enableEducationalDropdown = false,
  educationalDropdownContentCards,
  maxResults = 6,
  ...domainSearchBoxProps
}) {
  const IconElement = icon ? icon : /*#__PURE__*/_react.default.createElement(_icon.Icon, {
    className: buttonText && iconStyles,
    name: "search"
  });
  const formattedId = `id-${id}`;
  const initQueryValue = typeof document !== 'undefined' && id ? document.getElementById(formattedId)?.value : '';
  const [query, setQuery] = (0, _react.useState)(initQueryValue || '');
  const [prefetched, setPrefetched] = (0, _react.useState)(false);
  const [isFocused, setIsFocused] = (0, _react.useState)(false);
  const [suggestions, setSuggestions] = (0, _react.useState)([]);
  const [wrapperElevated, setWrapperElevated] = (0, _react.useState)(false);
  const overlayOpen = enableEducationalDropdown && isFocused && suggestions.length > 0;
  const elevationTimerRef = (0, _react.useRef)(null);
  const handleSuggestionsFetch = data => {
    const parsedLimit = Number(maxResults);
    setSuggestions(Array.isArray(data) && Number.isFinite(parsedLimit) && parsedLimit > 0 ? data.slice(0, parsedLimit) : data);
  };

  // Manage wrapper elevation timing to prevent z-index flashing
  (0, _react.useEffect)(() => {
    if (elevationTimerRef.current) {
      clearTimeout(elevationTimerRef.current);
      elevationTimerRef.current = null;
    }
    if (overlayOpen) {
      setWrapperElevated(true);
    } else {
      elevationTimerRef.current = setTimeout(() => {
        setWrapperElevated(false);
        elevationTimerRef.current = null;
      }, 200);
    }
    return () => {
      if (elevationTimerRef.current) {
        clearTimeout(elevationTimerRef.current);
        elevationTimerRef.current = null;
      }
    };
  }, [overlayOpen]);
  const wrapperRef = (0, _react.useRef)(null);
  const styling = {
    fontSize: height === 'small' ? `calc(${_intents.default.ux.textInput.fontSize} * 0.81)` : _intents.default.ux.textInput.fontSize,
    height: height === 'small' ? `3.125rem` : `3.875rem`,
    borderRadius: _intents.default.ux.control.borderRadius,
    buttonRadius: `0 ${_intents.default.ux.control.borderRadius} ${_intents.default.ux.control.borderRadius} 0`,
    hideInnerBorders: true
  };
  const formatUrlString = value => {
    const pageUrl = new URL(global.location.href);
    const {
      searchParams: pageSearchParams
    } = pageUrl;
    const pageItc = pageSearchParams.get('itc');
    let urlString = endpointType?.url || url;
    if (endpointType?._name === 'domain_transfer') {
      urlString = (0, _base.urlJoin)(urlString, value);
    }
    if (urlParams) {
      urlString += urlParams;
    }
    const redirectToUrl = new URL(urlString);
    const {
      searchParams
    } = redirectToUrl;
    const urlItc = searchParams.get('itc');
    if (endpointType?._name !== 'domain_transfer') {
      const fields = endpointType?.searchFieldName.split(',');
      fields.map(field => searchParams.set(field, value));
    }
    if (checkAvail) {
      searchParams.set('checkAvail', '1');
    }
    if (personalizationKey) {
      searchParams.set(PERSONALIZATION_KEY_NAME, personalizationKey);
    }
    if (pageItc || urlItc) {
      searchParams.set('itc', pageItc || urlItc);
    }
    return redirectToUrl.href;
  };
  const search = value => {
    if (!value) return;
    const searchValue = selectedTld && !value.includes('.') ? `${value}${selectedTld}` : value;
    const searchUrl = formatUrlString(searchValue);
    if (handleOnSearch) {
      handleOnSearch(searchUrl);
    } else if (global) {
      global.location.assign(searchUrl);
    }
  };
  const handlePrefetch = () => {
    if (!prefetched && prefetchSerpAssets) {
      (0, _assets.fetchAssets)(prefetchAssets);
      setPrefetched(true);
    }
  };
  (0, _keyboard.useKeydown)('Escape', () => {
    setIsFocused(false);
  });
  const handleSuggestionSelect = value => {
    setQuery(value);
    if (typeof domainSearchBoxProps?.onChange === 'function') {
      domainSearchBoxProps.onChange(value);
    }
  };
  return /*#__PURE__*/_react.default.createElement("div", {
    ref: wrapperRef,
    style: {
      position: 'relative'
    },
    className: wrapperElevated ? elevatedWrapperClass : null,
    onFocusCapture: () => setIsFocused(true)
  }, enableEducationalDropdown && /*#__PURE__*/_react.default.createElement(_overlayPortal.default, {
    open: overlayOpen,
    className: overlayClass,
    onClick: () => setIsFocused(false)
  }), /*#__PURE__*/_react.default.createElement(_domainSearchBox.DomainSearchBox, (0, _extends2.default)({
    id: formattedId,
    dataPartial: partial,
    clearable: false,
    onKeyDown: handlePrefetch,
    onChange: setQuery,
    onSearch: search,
    query: query,
    env: env,
    icon: IconElement,
    buttonText: buttonText,
    autoComplete: browserAutoComplete ? 'on' : 'off',
    autocompleteMode: autocompleteMode,
    ariaLabels: {
      searchLabel: ariaLabelInput,
      searchButton: ariaLabelButton
    },
    "data-cy": "domain-search",
    "data-version": "1.4.9",
    styling: styling,
    className: domainSearchStyles,
    name: "searchText"
  }, enableEducationalDropdown && {
    forceHideSuggest: true,
    domainNamesFetchCallback: handleSuggestionsFetch
  }, _tracking.default.create(_name, true, isPromo, trackingCustomProperties), domainSearchBoxProps)), enableEducationalDropdown && /*#__PURE__*/_react.default.createElement(_educationalDropdown.default, (0, _extends2.default)({
    id: `${formattedId}-flyout`,
    _name: _name,
    items: suggestions,
    open: isFocused,
    onSelect: handleSuggestionSelect,
    onClose: () => setIsFocused(false),
    parentRef: wrapperRef,
    isPromo: isPromo,
    trackingCustomProperties: trackingCustomProperties,
    isOnOverlay: overlayOpen
  }, educationalDropdownContentCards && {
    contentCards: educationalDropdownContentCards
  })));
}
DomainSearch.displayName = 'DomainSearch';
DomainSearch.propTypes = {
  id: _propTypes.default.string,
  _name: _propTypes.default.string,
  endpointType: _propTypes.default.shape({
    url: _propTypes.default.string,
    searchFieldName: _propTypes.default.string,
    _name: _propTypes.default.string
  }),
  urlParams: _propTypes.default.string,
  env: _propTypes.default.string,
  url: _propTypes.default.string,
  checkAvail: _propTypes.default.bool,
  personalizationKey: _propTypes.default.string,
  ariaLabelButton: _propTypes.default.string,
  ariaLabelInput: _propTypes.default.string,
  browserAutoComplete: _propTypes.default.bool,
  autocompleteMode: _propTypes.default.oneOf(['query', 'domain', '.']),
  handleOnSearch: _propTypes.default.func,
  icon: _propTypes.default.oneOfType([_propTypes.default.bool, _propTypes.default.elementType]),
  isPromo: _propTypes.default.bool,
  buttonText: _propTypes.default.string,
  height: _propTypes.default.oneOf(['default', 'small']),
  trackingCustomProperties: _propTypes.default.object,
  prefetchAssets: _propTypes.default.arrayOf(_propTypes.default.string),
  prefetchSerpAssets: _propTypes.default.bool,
  partial: _propTypes.default.string,
  selectedTld: _propTypes.default.string,
  enableEducationalDropdown: _propTypes.default.bool,
  educationalDropdownContentCards: _propTypes.default.arrayOf(_propTypes.default.shape({
    icon: _propTypes.default.string.isRequired,
    title: _propTypes.default.string.isRequired,
    description: _propTypes.default.string.isRequired,
    image: _propTypes.default.object
  })),
  maxResults: _propTypes.default.number,
  ..._domainSearchBox.DomainSearchBox.propTypes
};